import { type ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateFilter, ExportStatusFilter, SortFilter } from 'widgets';
import { type AllResultsResponse, ExportStatusValue } from 'types/result';
import { ExportStatusIcon } from 'widgets/home/ExportStatusIcon';
import {
  ActionButton,
  ActionButtons,
  ChevronDownIcon,
  ChevronUpIcon,
  ClearFiltersButton,
  ControlsContainer,
  EntryCount,
  FailedBadge,
  FilterActiveCount,
  FilterContainer,
  FilterIconStyled,
  FilterRow,
  NotExportedBadge,
  SearchContainer,
  SearchIconStyled,
  SearchIconWrapper,
  SearchInput,
  XIconStyled,
} from './HomeFilterHeader.styles';
import { useHomeFilters } from 'providers';

interface HomeFilterHeaderProps {
  data?: AllResultsResponse;
}

export const HomeFilterHeader = ({ data }: HomeFilterHeaderProps) => {
  const { t } = useTranslation();
  const { filters, setFilters } = useHomeFilters();
  const [isFilterRowOpen, setIsFilterRowOpen] = useState(false);

  const entryCount = data?.count || 0;
  const notExportedCount = data?.notExportedCount || 0;
  const failedCount = data?.failedCount || 0;

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, searchQuery: e.target.value });
  };

  const handleDateRangeChange = (dateRange: typeof filters.dateRange) => {
    setFilters({ ...filters, dateRange });
  };

  const handleOrderingChange = (ordering: typeof filters.ordering) => {
    setFilters({ ...filters, ordering });
  };

  const handleExportStatusClick = (status: ExportStatusValue | undefined) => {
    setFilters({
      ...filters,
      exportStatusFilter: filters.exportStatusFilter === status ? undefined : status,
    });
  };

  const toggleExportStatusFilter = (status: ExportStatusValue | undefined) => {
    setFilters({
      ...filters,
      exportStatusFilter: filters.exportStatusFilter === status ? undefined : status,
    });
  };

  const clearExportStatusFilter = () => {
    setFilters({ ...filters, exportStatusFilter: undefined });
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.exportStatusFilter) count++;
    return count;
  }, [filters.exportStatusFilter]);

  return (
    <>
      <FilterContainer>
        <DateFilter dateRange={filters.dateRange} onDateRangeChange={handleDateRangeChange} />
        <SearchContainer>
          <SearchIconWrapper>
            <SearchIconStyled />
          </SearchIconWrapper>
          <SearchInput
            placeholder={t('home.searchPlaceholder')}
            value={filters.searchQuery}
            onChange={handleSearchChange}
          />
        </SearchContainer>
      </FilterContainer>
      <ControlsContainer>
        <EntryCount role="status">
          {t('home.entryCount', { count: entryCount })}
          {notExportedCount > 0 && (
            <NotExportedBadge
              aria-label={`${t('home.exportStatusLabels.exportNotStarted')}: ${notExportedCount}`}
              onClick={() => handleExportStatusClick(ExportStatusValue.NoStatusFilter)}
            >
              <ExportStatusIcon status={undefined} />
              {notExportedCount}
            </NotExportedBadge>
          )}
          {failedCount > 0 && (
            <FailedBadge
              aria-label={`${t('home.exportStatusLabels.failed')}: ${failedCount}`}
              onClick={() => handleExportStatusClick(ExportStatusValue.Failed)}
            >
              <ExportStatusIcon status={ExportStatusValue.Failure} />
              {failedCount}
            </FailedBadge>
          )}
        </EntryCount>
        <ActionButtons>
          <ActionButton onClick={() => setIsFilterRowOpen(!isFilterRowOpen)}>
            <FilterIconStyled />
            {t('home.filter')}
            {activeFilterCount > 0 && <FilterActiveCount>{activeFilterCount}</FilterActiveCount>}
            {isFilterRowOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </ActionButton>
          <SortFilter ordering={filters.ordering} onOrderingChange={handleOrderingChange} />
        </ActionButtons>
      </ControlsContainer>
      {isFilterRowOpen && (
        <FilterRow>
          <ExportStatusFilter
            exportStatusFilter={filters.exportStatusFilter}
            onToggleExportStatusFilter={toggleExportStatusFilter}
          />
          <ClearFiltersButton onClick={clearExportStatusFilter}>
            <XIconStyled />
          </ClearFiltersButton>
        </FilterRow>
      )}
    </>
  );
};
